import { Currency } from '@/store/currenciesStore';
import { useSettingsStore } from '@/store/settingsStore';

/**
 * Format a currency amount according to user preferences
 * @param amount The amount to format
 * @param currency The currency object or code
 * @param options Formatting options
 * @returns Formatted currency string
 */
export function formatCurrency(
  amount: number,
  currency?: Currency | string,
  options: {
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
    showSymbol?: boolean;
    showCode?: boolean;
    numberFormat?: string;
  } = {}
): string {
  // Get settings from options or defaults
  const {
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
    showSymbol = true,
    showCode = false,
    numberFormat
  } = options;

  // Determine currency details
  let symbol = '$';
  let code = 'USD';
  
  if (typeof currency === 'object' && currency !== null) {
    symbol = currency.symbol || '$';
    code = currency.code || 'USD';
  } else if (typeof currency === 'string') {
    code = currency;
    // We don't have the symbol, so we'll use the code if showSymbol is true
    symbol = currency;
  }

  // Parse number format to determine separators and grouping
  const format = numberFormat || '#,##0.00';
  const useCommaDecimal = format.includes(',00');
  const decimalSeparator = useCommaDecimal ? ',' : '.';
  const groupingSeparator = useCommaDecimal ? '.' : ',';
  const useGrouping = format.includes('#,') || format.includes('#.');

  // Format the number according to preferences
  const formattedNumber = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits,
    maximumFractionDigits,
    useGrouping,
    signDisplay: 'never'
  }).format(Math.abs(amount));

  // Replace the default separators with the user's preferred ones
  let formattedWithUserSeparators = formattedNumber;
  if (useCommaDecimal) {
    formattedWithUserSeparators = formattedNumber
      .replace(/,/g, 'TEMP_GROUPING')
      .replace(/\./g, decimalSeparator)
      .replace(/TEMP_GROUPING/g, groupingSeparator);
  }

  // Build the final result
  let result = '';
  
  if (showSymbol) {
    result += symbol + ' ';
  }
  
  result += formattedWithUserSeparators;
  
  if (showCode) {
    result += ' ' + code;
  }

  return result;
}

/**
 * Format a number according to user preferences
 * @param value The number to format
 * @param numberFormat The number format pattern
 * @param options Additional formatting options
 * @returns Formatted number string
 */
export function formatNumber(
  value: number,
  numberFormat?: string,
  options: {
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  } = {}
): string {
  const {
    minimumFractionDigits = 2,
    maximumFractionDigits = 2
  } = options;

  // Parse number format to determine separators and grouping
  const format = numberFormat || '#,##0.00';
  const useCommaDecimal = format.includes(',00');
  const decimalSeparator = useCommaDecimal ? ',' : '.';
  const groupingSeparator = useCommaDecimal ? '.' : ',';
  const useGrouping = format.includes('#,') || format.includes('#.');

  // Format the number according to preferences
  const formattedNumber = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits,
    maximumFractionDigits,
    useGrouping,
  }).format(value);

  // Replace the default separators with the user's preferred ones
  if (useCommaDecimal) {
    return formattedNumber
      .replace(/,/g, 'TEMP_GROUPING')
      .replace(/\./g, decimalSeparator)
      .replace(/TEMP_GROUPING/g, groupingSeparator);
  }

  return formattedNumber;
}
